import { faArrowUpFromArc } from "@fortawesome/pro-regular-svg-icons/faArrowUpFromArc";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { FEED_CARD_WIDTH, FEED_RIGHT_PANEL_ID } from "app/appConstants";
import { GenericAbortSignal } from "axios";
import { MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { FeedItemDto, FeedType } from "generated/CoreApi_v1";
import { useMentionInterceptor } from "hooks/useMentionInterceptor";
import { useReferredState } from "hooks/useReferredState";
import { INSIDE_LAYOUT_CONTENT_CONTAINER_ID } from "layouts/common/inside/MprcLayoutInside";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps, ListOnScrollProps, VariableSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { PublicationFeed } from "views/knowledge/components/card";
import { CardManager } from "views/knowledge/components/card/CardManager";
import { CardSkeleton } from "views/knowledge/components/card/CardSkeleton";

////////////////////////////////////////////////////////////////////////////////

type ItemData = {
    items: PublicationFeed[];
    otherInfo?: any;
    useInfiniteLoading?: boolean;
};

interface IDictionary {
    [id: string]: number;
}

////////////////////////////////////////////////////////////////////////////////

interface FeedManagerProps {
    feedType: FeedType;
    otherInfo?: any;
    minBatchSize?: number;
    useInfiniteLoading?: boolean;
    onGetData: (startIndex: number, endIndex: number, signal: GenericAbortSignal | undefined) => Promise<PublicationFeed[]>;
    onReload?: (publicationId: string) => void;
    onEmptyDataset?: () => void;
    previousPublicationId?: string | null;
    onGobackTop?: () => void;
    onError?: (error: any) => void;
}

export type FeedManagerHandle = {
    reset: () => void;
    scrolled: () => boolean;
};

const FeedManagerComponent: React.ForwardRefRenderFunction<FeedManagerHandle, FeedManagerProps> = (props, forwardRef) => {

    const { feedType, otherInfo, minBatchSize = 20, useInfiniteLoading, previousPublicationId, onGobackTop, onGetData, onError, onEmptyDataset, onReload } = props;
    const { t } = useTranslation("knowledge");
    const api = useCoreApi();

    const [error, setError] = useState<any>(null);
    const [, setLoading, refLoading] = useReferredState<boolean>(false);
    const [items, setItems] = useState<PublicationFeed[]>([]);
    const endOfRecords = useRef<boolean>(false);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);
    const hasMountedRef = useRef(false);
    const [mentionDialog] = useMentionInterceptor("feedManagerContainer");

    let listRef = useRef<VariableSizeList<{ items: FeedItemDto[]; useInfiniteLoading: boolean | undefined; }> | null>(null);
    const rowHeights = useRef<IDictionary>({});
    const abortControllerRef = useRef<AbortController>(undefined);

    ////////////////////////////////////////////////////////////////////////////////

    useImperativeHandle(forwardRef, () => ({ reset, scrolled }));

    ///
    const reset = () => {
        setItems([]);
        if (hasMountedRef.current) {
            listRef.current?.resetAfterIndex(0);
            infiniteLoaderRef.current?.resetloadMoreItemsCache(true);
            rowHeights.current = {};
            loadData(0, 0);
        }
    };

    ///
    const scrolled = () => {
        return windowRef!.scrollTop > 0;
    };

    ////////////////////////////////////////////////////////////////////////////////

    ///
    function getRowHeight(index: number) {
        return rowHeights.current[index] || 100;
    }

    ///
    function setRowHeight(index: number, size: number) {
        if (rowHeights.current[index] == undefined || rowHeights.current[index] != size) {
            listRef.current?.resetAfterIndex(index);
            rowHeights.current = { ...rowHeights.current, [index]: size };
        }
    }

    ///
    async function loadData(startIndex: number, endIndex: number) {
        setLoading(true);
        setError(null);

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        try {
            var data = await onGetData(startIndex, minBatchSize + endIndex, abortControllerRef.current.signal);

            if (startIndex == 0) {
                // Inicializa o array de itens.
                setItems(data);
            }
            else {
                // Adiciona mais itens no array.
                setItems([...items, ...data]);
            }

            // Há mais registros?
            endOfRecords.current = data.length < (minBatchSize + endIndex - startIndex + 1);

            if (startIndex == 0 && data.length == 0 && onEmptyDataset)
                onEmptyDataset();

            setLoading(false);
        }
        catch (e) {
            endOfRecords.current = true;
            if (!isCanceledError(e)) {
                setError(e);
                setLoading(false);
            }
        }
    }

    ///
    function onDataItemChanged(index: number, data: Partial<PublicationFeed>) {
        setItems(prevState => {
            const newState = prevState.map((obj, i) => {
                if (i == index) {
                    return { ...obj, ...data };
                }
                return obj;
            });
            return newState;
        });
    }

    ///
    async function reload(index: number, rowRef: HTMLDivElement) {
        if (index < items.length) {
            try {
                const paperRoot = rowRef.getElementsByClassName("MuiPaper-root");
                if (paperRoot.length > 0) {
                    (paperRoot[0] as HTMLElement).style.backgroundColor = "rgba(0, 0, 0, 0.1)";
                }

                var newData = await api.v1.knowledgeFeed.getFeedItem({ publicationId: items[index].publicationId, markRead: false });
                if (newData.data) {
                    onDataItemChanged(index, newData.data);
                }
            }
            catch (e) {
                console.error(e);
            }
        }
    }

    ///
    const markPublicationRead = (index: number) => {
        return api.v1.knowledgeFeed.markPublicationRead({ publicationId: items[index].publicationId });
    };

    ////////////////////////////////////////////////////////////////////////////////

    /**
     * Renderiza uma linha da Lista.
     * @param props
     */
    // Renderiza uma linha da Lista.
    function RenderRow(props: ListChildComponentProps<ItemData>) {
        const rowRef = useRef<HTMLDivElement>(null);
        const { index, style, data: { items, useInfiniteLoading } } = props;

        useEffect(() => {
            if (rowRef.current) {
                setRowHeight(index, rowRef.current.clientHeight);
            }
        }, [rowRef]);

        if (index > items.length - 1) {
            return useInfiniteLoading !== true || items.length > 0
                ? (
                    <Box style={style} sx={{ mt: 1 }}>
                        <MprcSpinnerIndicator />
                    </Box>
                )
                : null;
        }

        return (
            <div id={`item-${index}`} key={index} style={style}>
                <div ref={rowRef} >
                    {
                        (items[index] as any)["reloading"] === true && <MprcSpinnerIndicator />
                    }
                    <CardManager
                        feedType={feedType}
                        otherInfo={otherInfo}
                        pub={items[index]}
                        onNeedResetRowHeight={() => setRowHeight(index, rowRef.current!.clientHeight)}
                        onFeedItemChanged={(data) => onDataItemChanged(index, data)}
                        onFeedReload={reset}
                        onReload={() => {
                            if (onReload) onReload(items[index].publicationId);
                            else reload(index, rowRef.current!);
                        }}
                        onMarkRead={() => markPublicationRead(index)}
                    />
                </div>
            </div>
        );
    }

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const loadNextPage = (startIndex: number, stopIndex: number) => {
        return loadData(startIndex, stopIndex);
    };

    // Se há mais itens para ser carregado, adicionamos UMA LINHA para que o Indicador de Carregamento apareça.
    const itemCount = !endOfRecords.current ? items.length + 1 : items.length;

    // Permite apenas uma página de items pode vez.
    const loadMoreItems = refLoading.current == true ? () => { } : loadNextPage;

    // Verifica se a linha está carregada, exceto o Indicador de Carregamento.
    const isItemLoaded = (index: number) => {
        return endOfRecords.current || index < items.length;
    };

    ////////////////////////////////////////////////////////////////////////////////////
    /// Entra em modo de pesquisa.
    useEffect(() => {
        hasMountedRef.current = true;
    }, []);

    ////////////////////////////////////////////////////////////////////////////////////
    /// Controle do Scroll
    const [windowRef, setWindowRef] = useState<HTMLElement | null>(null);
    const outerRef = useRef<HTMLDivElement | null>(null);
    const ccRef = useRef<HTMLElement | null>(null);
    const frpRef = useRef<HTMLElement | null>(null);
    const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);


    /** Calcula a área ocupada ACIMA do Infinite Scroll */
    useEffect(() => {

        function handleWindowScroll(this: HTMLElement) {
            if (timeout.current) clearTimeout(timeout.current);
            timeout.current = setTimeout(() => {
                timeout.current = null;
                const { offsetTop = 0 } = outerRef.current || {};
                const scrollTop = this.scrollTop - offsetTop;
                listRef.current?.scrollTo(scrollTop);
            }, 150);
        }

        var w = document.getElementById(INSIDE_LAYOUT_CONTENT_CONTAINER_ID);
        ccRef.current = document.getElementById(INSIDE_LAYOUT_CONTENT_CONTAINER_ID);
        frpRef.current = document.getElementById(FEED_RIGHT_PANEL_ID);
        w?.addEventListener('scroll', handleWindowScroll);

        setWindowRef(w);

        return () => {
            w?.removeEventListener('scroll', handleWindowScroll);
            if (timeout.current) clearTimeout(timeout.current);
        };
    }, [outerRef.current]);

    const onScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }: ListOnScrollProps) => {
        if (!scrollUpdateWasRequested) return;

        const top = windowRef!.scrollTop;
        const { offsetTop = 0 } = outerRef.current || {};
        scrollOffset += Math.min(top, offsetTop);

        if (scrollOffset !== top) {
            windowRef!.scrollTo(0, scrollOffset);
        }
    }, [windowRef]);

    ////////////////////////////////////////////////////////////////////////////////////

    const feedRendered = useMemo(() => (
        <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
            ref={infiniteLoaderRef}
        >
            {
                ({ onItemsRendered, ref }) => (
                    <VariableSizeList
                        className="vsl"
                        style={{ overflow: "visible", margin: "0 auto", height: "100%" }}
                        layout="vertical"
                        height={window.innerHeight}
                        width={FEED_CARD_WIDTH}
                        useIsScrolling
                        itemData={{ items, useInfiniteLoading }}
                        onItemsRendered={onItemsRendered}
                        ref={list => {
                            // Pass List ref through to InfiniteLoader
                            ref(list);
                            // And store a copy for yourself.
                            listRef.current = list;
                        }}
                        itemCount={itemCount}
                        itemSize={getRowHeight}
                        onScroll={onScroll}
                        outerRef={outerRef}
                    >
                        {RenderRow}
                    </VariableSizeList>
                )
            }
        </InfiniteLoader>
    ), [items]);

    /// Aborta última requisição de dados.
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    /// Tratamento de Erros
    if (error) {
        if (onError) {
            // O chamador irá processar o erro, então não rederiza o erro por aqui.
            onError(error);
            return null;
        }

        /// Caso contrário, exibe o erro.
        return <MprcRequestErrorReport error={error} />;
    }

    return (
        <div id="feedManagerContainer">
            {mentionDialog}

            {refLoading.current === true &&
                <Box sx={{ margin: "0 auto", width: FEED_CARD_WIDTH }}>
                    <CardSkeleton visible={refLoading.current === true && useInfiniteLoading == true && items.length == 0} />
                </Box>
            }

            {
                refLoading.current === false && previousPublicationId != null && items.length > 0 && items[0].rowNumber > 1 &&
                <Box sx={{ margin: "0 auto", width: FEED_CARD_WIDTH, mb: 1 }}>
                    <MprcLink onClick={onGobackTop} title={t("Feed.GobackStartTooltip")}>
                        <FontAwesomeIcon icon={faArrowUpFromArc} size="2x" />
                    </MprcLink>
                </Box>
            }

            {windowRef != null && feedRendered}
        </div>
    );
};

export const FeedManager = React.forwardRef(FeedManagerComponent);
